<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import PublicSiteFooter from '@/components/publicSite/PublicSiteFooter.vue'
import PublicSiteHeader from '@/components/publicSite/PublicSiteHeader.vue'
import { buildModuleGuideImageUrl } from '@/data/moduleGuides'
import { getLocalizedModuleGuideBySlug, normalizeModuleGuideLocale } from '@/data/moduleGuides.localized'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const route = useRoute()
const { t, locale } = useI18n({ useScope: 'global' })

const guide = computed(() => getLocalizedModuleGuideBySlug(String(route.params.slug || ''), locale.value))
const relatedGuides = computed(() =>
  (guide.value?.related || [])
    .map(slug => getLocalizedModuleGuideBySlug(slug, locale.value))
    .filter((item): item is NonNullable<typeof item> => Boolean(item)),
)

const quickFacts = computed(() => {
  if (!guide.value) return []

  return [
    { label: t('publicSite.moduleGuide.quickFactsSetup'), value: guide.value.setupTime },
    { label: t('publicSite.moduleGuide.quickFactsDifficulty'), value: guide.value.difficulty },
    { label: t('publicSite.moduleGuide.quickFactsChannels'), value: guide.value.channels.join(' / ') },
    { label: t('publicSite.moduleGuide.quickFactsGoal'), value: guide.value.tenantGoal },
  ]
})

const imageUrl = buildModuleGuideImageUrl
const failedImages = ref<Record<string, boolean>>({})

const markImageAsFailed = (url: string) => {
  failedImages.value[url] = true
}

const hasImageFailed = (url: string) => Boolean(failedImages.value[url])

const setupSEO = () => {
  if (typeof document === 'undefined') return

  const pageTitle = guide.value
    ? `${guide.value.title} - ${t('publicSite.moduleGuide.seoTitleSuffix')}`
    : t('publicSite.moduleGuide.seoTitleFallback')
  const description = guide.value?.summary || t('publicSite.moduleGuide.seoDescriptionFallback')
  const url = window.location.href
  const image = guide.value ? imageUrl(guide.value.heroImage) : `${window.location.origin}/images/sito-seo.jpg`

  document.title = pageTitle

  const setMeta = (name: string, content: string, isProperty = false) => {
    let el = document.querySelector(`meta[${isProperty ? 'property' : 'name'}="${name}"]`)
    if (!el) {
      el = document.createElement('meta')
      if (isProperty) el.setAttribute('property', name)
      else el.setAttribute('name', name)
      document.head.appendChild(el)
    }
    el.setAttribute('content', content)
  }

  setMeta('description', description)
  setMeta('og:title', pageTitle, true)
  setMeta('og:description', description, true)
  setMeta('og:url', url, true)
  setMeta('og:type', 'article', true)
  setMeta('og:image', image, true)
  setMeta('twitter:card', 'summary_large_image')
  setMeta('twitter:title', pageTitle)
  setMeta('twitter:description', description)
  setMeta('twitter:image', image)
}

onMounted(() => {
  setupSEO()
})
watch([guide, locale], setupSEO)
</script>

<template>
  <div class="module-guide-page">
    <PublicSiteHeader />

    <div v-if="guide" class="guide-content">
      <section class="hero-section">
        <div class="container hero-grid">
          <div class="hero-copy">
            <div class="eyebrow-chip" :style="{ color: guide.color, borderColor: `${guide.color}55`, backgroundColor: `${guide.color}18` }">
              {{ guide.eyebrow }}
            </div>
            <h1 class="hero-title">{{ guide.title }}</h1>
            <p class="hero-summary">{{ guide.summary }}</p>
            <p class="hero-overview">{{ guide.overview }}</p>

            <div class="hero-goal-card">
              <span class="goal-label">{{ t('publicSite.moduleGuide.goalLabel') }}</span>
              <p class="goal-text">{{ guide.tenantGoal }}</p>
            </div>

            <div class="hero-actions">
              <VBtn color="primary" to="/register" class="btn-glow text-none px-8">
                {{ t('publicSite.moduleGuide.activateModule') }}
              </VBtn>
              <VBtn variant="outlined" color="white" href="/#features" class="text-none px-8">
                {{ t('publicSite.shared.exploreOtherModules') }}
              </VBtn>
            </div>
          </div>

          <div class="hero-panel">
            <div class="hero-media-card">
              <img
                v-if="!hasImageFailed(imageUrl(guide.heroImage))"
                :src="imageUrl(guide.heroImage)"
                :alt="guide.heroImage.alt"
                class="hero-preview-image"
                loading="eager"
                @error="markImageAsFailed(imageUrl(guide.heroImage))"
              >
              <div
                v-else
                class="image-fallback hero-fallback"
                :style="{ '--fallback-accent': guide.color }"
              >
                <div class="fallback-icon-shell">
                  <VIcon :icon="guide.icon" size="46" />
                </div>
                <span class="fallback-kicker">{{ t('publicSite.shared.previewTemporarilyUnavailable') }}</span>
                <h3 class="fallback-title">{{ guide.title }}</h3>
                <p class="fallback-text">{{ guide.heroImage.caption }}</p>
              </div>
              <div class="hero-media-overlay">
                <span class="hero-media-kicker">{{ t('publicSite.shared.realisticPreview') }}</span>
                <p class="hero-media-caption">{{ guide.heroImage.caption }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="quickfacts-section">
        <div class="container">
          <div class="quickfacts-grid">
            <div v-for="fact in quickFacts" :key="fact.label" class="quickfact-card">
              <span class="quickfact-label">{{ fact.label }}</span>
              <span class="quickfact-value">{{ fact.value }}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="preview-section">
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">{{ t('publicSite.moduleGuide.previewTitle') }}</h2>
            <p class="section-description">{{ t('publicSite.moduleGuide.previewDescription') }}</p>
          </div>

          <VRow>
            <VCol v-for="image in guide.gallery" :key="image.alt" cols="12" md="4">
              <VCard class="preview-card">
                <img
                  v-if="!hasImageFailed(imageUrl(image))"
                  :src="imageUrl(image)"
                  :alt="image.alt"
                  class="preview-image"
                  loading="lazy"
                  @error="markImageAsFailed(imageUrl(image))"
                >
                <div
                  v-else
                  class="image-fallback gallery-fallback"
                  :style="{ '--fallback-accent': guide.color }"
                >
                  <div class="fallback-icon-shell">
                    <VIcon :icon="guide.icon" size="38" />
                  </div>
                  <p class="fallback-text">{{ image.caption }}</p>
                </div>
                <div class="preview-copy">
                  <p class="preview-caption">{{ image.caption }}</p>
                </div>
              </VCard>
            </VCol>
          </VRow>
        </div>
      </section>

      <section class="content-section">
        <div class="container content-grid">
          <VCard class="content-card">
            <div class="card-head">
              <VIcon icon="tabler-target-arrow" size="22" />
              <h3 class="card-title">{{ t('publicSite.moduleGuide.usefulFor') }}</h3>
            </div>
            <ul class="detail-list">
              <li v-for="item in guide.idealFor" :key="item">{{ item }}</li>
            </ul>
          </VCard>

          <VCard class="content-card">
            <div class="card-head">
              <VIcon icon="tabler-plug-connected" size="22" />
              <h3 class="card-title">{{ t('publicSite.moduleGuide.prerequisites') }}</h3>
            </div>
            <ul class="detail-list">
              <li v-for="item in guide.prerequisites" :key="item">{{ item }}</li>
            </ul>
          </VCard>

          <VCard class="content-card content-card-wide">
            <div class="card-head">
              <VIcon icon="tabler-settings-cog" size="22" />
              <h3 class="card-title">{{ t('publicSite.moduleGuide.activationSteps') }}</h3>
            </div>
            <ol class="number-list">
              <li v-for="item in guide.activationSteps" :key="item">{{ item }}</li>
            </ol>
          </VCard>

          <VCard class="content-card">
            <div class="card-head">
              <VIcon icon="tabler-device-mobile" size="22" />
              <h3 class="card-title">{{ t('publicSite.moduleGuide.appExperience') }}</h3>
            </div>
            <ul class="detail-list">
              <li v-for="item in guide.appExperience" :key="item">{{ item }}</li>
            </ul>
          </VCard>

          <VCard class="content-card">
            <div class="card-head">
              <VIcon icon="tabler-rocket" size="22" />
              <h3 class="card-title">{{ t('publicSite.moduleGuide.outcomes') }}</h3>
            </div>
            <ul class="detail-list">
              <li v-for="item in guide.outcomes" :key="item">{{ item }}</li>
            </ul>
          </VCard>

          <VCard class="content-card">
            <div class="card-head">
              <VIcon icon="tabler-briefcase" size="22" />
              <h3 class="card-title">{{ t('publicSite.moduleGuide.useCases') }}</h3>
            </div>
            <ul class="detail-list">
              <li v-for="item in guide.useCases" :key="item">{{ item }}</li>
            </ul>
          </VCard>

          <VCard class="content-card">
            <div class="card-head">
              <VIcon icon="tabler-bulb" size="22" />
              <h3 class="card-title">{{ t('publicSite.moduleGuide.bestPractices') }}</h3>
            </div>
            <ul class="detail-list">
              <li v-for="item in guide.bestPractices" :key="item">{{ item }}</li>
            </ul>
          </VCard>
        </div>
      </section>

      <section class="insights-section">
        <div class="container insights-grid">
          <VCard class="content-card">
            <div class="card-head">
              <VIcon icon="tabler-checklist" size="22" />
              <h3 class="card-title">{{ t('publicSite.moduleGuide.goLiveChecklist') }}</h3>
            </div>
            <ul class="detail-list detail-list-check">
              <li v-for="item in guide.goLiveChecklist" :key="item">{{ item }}</li>
            </ul>
          </VCard>

          <VCard class="content-card">
            <div class="card-head">
              <VIcon icon="tabler-chart-donut" size="22" />
              <h3 class="card-title">{{ t('publicSite.moduleGuide.kpis') }}</h3>
            </div>
            <div class="kpi-list">
              <span v-for="item in guide.kpis" :key="item" class="kpi-chip">{{ item }}</span>
            </div>
          </VCard>
        </div>
      </section>

      <section class="faq-section">
        <div class="container">
          <div class="section-header faq-header">
            <h2 class="section-title">{{ t('publicSite.moduleGuide.faqTitle') }}</h2>
            <p class="section-description">{{ t('publicSite.moduleGuide.faqDescription') }}</p>
          </div>

          <VExpansionPanels variant="accordion" class="faq-panels">
            <VExpansionPanel v-for="item in guide.faq" :key="item.question" class="faq-panel">
              <VExpansionPanelTitle class="faq-question">{{ item.question }}</VExpansionPanelTitle>
              <VExpansionPanelText>
                <p class="faq-answer">{{ item.answer }}</p>
              </VExpansionPanelText>
            </VExpansionPanel>
          </VExpansionPanels>
        </div>
      </section>

      <section class="related-section">
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">{{ t('publicSite.moduleGuide.relatedTitle') }}</h2>
            <p class="section-description">{{ t('publicSite.moduleGuide.relatedDescription') }}</p>
          </div>

          <VRow>
            <VCol v-for="item in relatedGuides" :key="item.slug" cols="12" md="6" lg="3">
              <VCard class="related-card" :to="`/modules/${item.slug}`">
                <div class="related-icon" :style="{ backgroundColor: `${item.color}22`, color: item.color }">
                  <VIcon :icon="item.icon" size="24" />
                </div>
                <h3>{{ item.title }}</h3>
                <p>{{ item.summary }}</p>
                <span class="related-link">{{ t('publicSite.shared.openGuide') }}</span>
              </VCard>
            </VCol>
          </VRow>
        </div>
      </section>
    </div>

    <div v-else class="not-found-shell">
      <div class="container py-16">
        <VCard class="not-found-card">
          <VIcon icon="tabler-alert-circle" size="56" color="warning" class="mb-6" />
          <h1 class="text-h3 font-weight-black not-found-title mb-4">{{ t('publicSite.moduleGuide.notFoundTitle') }}</h1>
          <p class="text-body-1 not-found-text mb-8">
            {{ t('publicSite.moduleGuide.notFoundDescription') }}
          </p>
          <div class="d-flex justify-center flex-wrap gap-4">
            <VBtn color="primary" href="/#features" class="text-none">{{ t('publicSite.shared.backToModules') }}</VBtn>
            <VBtn variant="outlined" color="primary" to="/register" class="text-none">{{ t('publicSite.shared.createYourApp') }}</VBtn>
          </div>
        </VCard>
      </div>
    </div>

    <PublicSiteFooter />
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;800&display=swap');

.module-guide-page {
  min-height: 100vh;
  background: #0F172A;
  color: #F8FAFC;
  font-family: 'Outfit', sans-serif;
  --text-strong: #FFF;
  --text-body: #E5EEF9;
  --text-soft: #E2E8F0;
  --text-muted: #CBD5E1;
}

.container {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 32px;
}

.hero-section {
  padding: 88px 0 32px;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 32px;
  align-items: stretch;
}

.hero-copy,
.hero-media-card,
.content-card,
.related-card,
.not-found-card,
.quickfact-card,
.preview-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 28px;
}

.hero-copy {
  padding: 40px;
}

.eyebrow-chip {
  display: inline-flex;
  align-items: center;
  border: 1px solid;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 20px;
}

.hero-title {
  font-size: clamp(2.6rem, 4vw, 4.6rem);
  line-height: 1.05;
  margin-bottom: 18px;
  font-weight: 800;
  letter-spacing: -0.04em;
  color: var(--text-strong);
}

.hero-summary {
  font-size: 1.2rem;
  color: var(--text-body);
  margin-bottom: 14px;
}

.hero-overview {
  color: var(--text-soft);
  line-height: 1.8;
  margin-bottom: 24px;
}

.hero-goal-card {
  padding: 18px 20px;
  border-radius: 22px;
  background: rgba(99, 102, 241, 0.12);
  border: 1px solid rgba(129, 140, 248, 0.18);
  margin-bottom: 24px;
}

.goal-label {
  display: block;
  color: #A5B4FC;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.goal-text {
  color: var(--text-strong);
  font-size: 1rem;
  line-height: 1.7;
  margin: 0;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.hero-media-card {
  position: relative;
  overflow: hidden;
  min-height: 100%;
}

.hero-preview-image {
  width: 100%;
  height: 100%;
  min-height: 420px;
  object-fit: cover;
  display: block;
}

.image-fallback {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  background:
    radial-gradient(circle at top right, color-mix(in srgb, var(--fallback-accent) 26%, transparent) 0%, transparent 42%),
    linear-gradient(160deg, rgba(30, 41, 59, 0.96) 0%, rgba(15, 23, 42, 0.98) 100%);
  color: var(--text-strong);
}

.hero-fallback {
  min-height: 420px;
  padding: 34px;
}

.gallery-fallback {
  min-height: 240px;
  padding: 24px;
}

.fallback-icon-shell {
  width: 72px;
  height: 72px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--fallback-accent) 20%, rgba(255, 255, 255, 0.04));
  color: var(--fallback-accent);
  margin-bottom: 18px;
}

.fallback-kicker {
  display: block;
  color: color-mix(in srgb, var(--fallback-accent) 72%, white);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 10px;
}

.fallback-title {
  color: var(--text-strong);
  font-size: 1.5rem;
  font-weight: 800;
  margin-bottom: 10px;
}

.fallback-text {
  color: var(--text-soft);
  line-height: 1.7;
  margin: 0;
}

.hero-media-overlay {
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 24px;
  padding: 18px 20px;
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.12) 0%, rgba(15, 23, 42, 0.88) 100%);
  backdrop-filter: blur(6px);
}

.hero-media-kicker {
  display: block;
  color: #C4B5FD;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 8px;
}

.hero-media-caption {
  color: var(--text-strong);
  margin: 0;
  line-height: 1.6;
}

.quickfacts-section {
  padding: 8px 0 24px;
}

.quickfacts-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.quickfact-card {
  padding: 22px;
}

.quickfact-label {
  display: block;
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 10px;
}

.quickfact-value {
  display: block;
  color: var(--text-strong);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.6;
}

.preview-section,
.content-section,
.insights-section,
.faq-section,
.related-section {
  padding: 16px 0 40px;
}

.section-header {
  margin-bottom: 28px;
}

.section-title {
  color: var(--text-strong);
  font-size: 2rem;
  font-weight: 800;
  margin-bottom: 10px;
}

.section-description {
  color: var(--text-soft);
  max-width: 860px;
  line-height: 1.8;
}

.preview-card {
  overflow: hidden;
  height: 100%;
}

.preview-image {
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  display: block;
}

.preview-copy {
  padding: 18px 20px 22px;
}

.preview-caption {
  margin: 0;
  color: var(--text-soft);
  line-height: 1.7;
}

.content-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}

.content-card {
  padding: 32px;
  color: var(--text-strong);
}

.content-card-wide {
  grid-column: span 2;
}

.card-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  font-weight: 700;
}

.card-title {
  color: var(--text-strong);
}

.detail-list,
.number-list {
  margin: 0;
  padding-left: 18px;
  color: var(--text-soft);
  line-height: 1.9;
}

.detail-list li + li,
.number-list li + li {
  margin-top: 10px;
}

.detail-list-check li::marker {
  color: #34D399;
}

.insights-grid {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 24px;
}

.kpi-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.kpi-chip {
  display: inline-flex;
  align-items: center;
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(99, 102, 241, 0.12);
  border: 1px solid rgba(129, 140, 248, 0.2);
  color: var(--text-body);
  font-weight: 700;
}

.faq-header {
  margin-bottom: 20px;
}

.faq-panels {
  display: grid;
  gap: 16px;
}

.faq-panel {
  background: rgba(255, 255, 255, 0.04) !important;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px !important;
  color: var(--text-strong);
}

.faq-question {
  color: var(--text-strong);
  font-weight: 700;
}

.faq-answer {
  color: var(--text-soft);
  line-height: 1.8;
  margin: 0;
}

.faq-panels :deep(.v-expansion-panel-text__wrapper) {
  padding-top: 0;
}

.faq-panels :deep(.v-expansion-panel-title__overlay) {
  background: transparent;
}

.related-card {
  height: 100%;
  padding: 24px;
  color: #F8FAFC;
  text-decoration: none;
  transition: transform 0.25s ease, border-color 0.25s ease, background 0.25s ease;
}

.related-card:hover {
  transform: translateY(-6px);
  border-color: rgba(99, 102, 241, 0.28);
  background: rgba(255, 255, 255, 0.06);
}

.related-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
}

.related-card h3 {
  font-size: 1.15rem;
  font-weight: 700;
  margin-bottom: 10px;
}

.related-card p {
  color: var(--text-soft);
  line-height: 1.7;
  margin-bottom: 18px;
}

.related-link {
  color: #A5B4FC;
  font-weight: 700;
}

.not-found-shell {
  padding: 80px 0;
}

.not-found-card {
  max-width: 760px;
  margin: 0 auto;
  padding: 48px;
  text-align: center;
}

.not-found-title {
  color: var(--text-strong);
}

.not-found-text {
  color: var(--text-soft);
}

.btn-glow {
  box-shadow: 0 0 24px rgba(99, 102, 241, 0.35);
}

@media (max-width: 1200px) {
  .hero-grid,
  .insights-grid,
  .content-grid,
  .quickfacts-grid {
    grid-template-columns: 1fr 1fr;
  }

  .content-card-wide {
    grid-column: span 2;
  }
}

@media (max-width: 1100px) {
  .hero-grid,
  .insights-grid,
  .content-grid,
  .quickfacts-grid {
    grid-template-columns: 1fr;
  }

  .content-card-wide {
    grid-column: auto;
  }
}

@media (max-width: 700px) {
  .container {
    padding: 0 20px;
  }

  .hero-copy,
  .hero-media-card,
  .content-card,
  .related-card,
  .not-found-card,
  .quickfact-card,
  .preview-card {
    border-radius: 22px;
  }

  .hero-section {
    padding-top: 48px;
  }

  .hero-preview-image {
    min-height: 280px;
  }

  .hero-media-overlay {
    left: 16px;
    right: 16px;
    bottom: 16px;
  }

  .section-title {
    font-size: 1.6rem;
  }
}
</style>
